import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { leadsApi, type SarvamSalesAgentMetricKey } from "@/api/leads";
import { useAuth } from "@/contexts/AuthContext";
import { formatDateTime } from "@/utils/dateUtils";

const METRIC_LABELS: Record<SarvamSalesAgentMetricKey, string> = {
  dnpRevived: "DNP — lead revived",
  dnpOpdBooked: "DNP agent — OPD booked",
  crmOpdBooked: "CRM agent — OPD booked",
  reminderConfirmed: "Reminder — appointment confirmed",
};

function istTodayKey(): string {
  const IST_OFFSET_MINUTES = 330;
  const now = new Date();
  const ist = new Date(now.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const d = String(ist.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function istMonthStartKey(): string {
  const IST_OFFSET_MINUTES = 330;
  const now = new Date();
  const ist = new Date(now.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function istDaysAgoKey(daysAgo: number): string {
  const IST_OFFSET_MINUTES = 330;
  const now = new Date();
  const ist = new Date(now.getTime() + IST_OFFSET_MINUTES * 60 * 1000);
  ist.setUTCDate(ist.getUTCDate() - daysAgo);
  const y = ist.getUTCFullYear();
  const m = String(ist.getUTCMonth() + 1).padStart(2, "0");
  const d = String(ist.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function CountLink(props: { count: number; onClick: () => void; accent?: string; large?: boolean }) {
  if (props.count <= 0) {
    return <span className="tabular-nums text-muted-foreground">0</span>;
  }
  return (
    <button
      type="button"
      className={`tabular-nums hover:underline font-medium ${props.large ? "text-2xl font-semibold" : ""} ${props.accent ?? "text-indigo-700"}`}
      onClick={props.onClick}
    >
      {props.count}
    </button>
  );
}

function KpiTile(props: { count: number; label: string; onClick: () => void; accent?: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{props.label}</p>
      <CountLink count={props.count} onClick={props.onClick} accent={props.accent} large />
    </div>
  );
}

export default function SarvamAgentOutcomesPage() {
  const { user } = useAuth();
  const isManager =
    user?.role === "SALES_MANAGER" ||
    user?.role === "HOSPITAL_ADMIN" ||
    user?.role === "SUPER_ADMIN";

  const [from, setFrom] = useState(istMonthStartKey);
  const [to, setTo] = useState(istTodayKey);
  const [repFilter, setRepFilter] = useState<string>("__all__");

  const staffIdParam =
    user?.role === "SALES_PERSON"
      ? user.id
      : repFilter === "__all__"
        ? undefined
        : repFilter;

  const queryKey = [
    "sarvam-agent-outcomes",
    user?.hospitalId,
    from,
    to,
    staffIdParam ?? "__all__",
  ] as const;

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await leadsApi.getSarvamSalesAgentDashboard({
        from,
        to,
        staffId: staffIdParam,
      });
      return res.data.data;
    },
    enabled: !!user?.hospitalId || user?.role === "SUPER_ADMIN",
    refetchInterval: 120_000,
    refetchOnWindowFocus: true,
  });

  const [drill, setDrill] = useState<{
    title: string;
    metric: SarvamSalesAgentMetricKey;
    date?: string;
    staffId?: string;
  } | null>(null);

  const drillQuery = useQuery({
    queryKey: ["sarvam-agent-outcomes-leads", drill?.metric, from, to, drill?.date, drill?.staffId],
    queryFn: async () => {
      if (!drill) return [];
      const res = await leadsApi.getSarvamSalesAgentMetricLeads({
        metric: drill.metric,
        from,
        to,
        date: drill.date,
        staffId: drill.staffId ?? staffIdParam,
      });
      return res.data.data.items as Array<{
        id: string;
        patientName?: string | null;
        phone?: string | null;
        sarvamOutcomeAt?: string | null;
      }>;
    },
    enabled: drill != null,
  });

  const openDrill = (
    metric: SarvamSalesAgentMetricKey,
    opts?: { date?: string; staffId?: string; repLabel?: string }
  ) => {
    const scope = opts?.date ? ` · ${opts.date}` : "";
    const rep = opts?.repLabel ? ` · ${opts.repLabel}` : "";
    setDrill({
      metric,
      date: opts?.date,
      staffId: opts?.staffId,
      title: `${METRIC_LABELS[metric]}${scope}${rep}`,
    });
  };

  const timeline = useMemo(() => data?.timeline ?? [], [data?.timeline]);
  const showTeamTable = isManager && repFilter === "__all__" && (data?.reps?.length ?? 0) > 0;

  return (
    <div className="space-y-6 p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">AI Outcomes</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Counts only <strong>your CRM-assigned leads</strong> (effective owner, same as billing tiles) with a completed AI
            voice call in the IST range and a clear outcome: DNP revival, DNP/CRM OPD booked on the call, or
            reminder confirmed. Distinct leads per day — not the same as all-time AI OPD booked counts on
            the Leads billing table.
          </p>
        </div>
        {isFetching && !isLoading ? (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Updating…
          </span>
        ) : null}
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Date range (IST)</CardTitle>
          <CardDescription>
            {data?.range
              ? `${data.range.fromKey} → ${data.range.toKey}`
              : "Month to date by default"}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4 items-end">
          <div className="space-y-1">
            <Label htmlFor="from">From</Label>
            <Input
              id="from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-[11rem]"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="to">To</Label>
            <Input
              id="to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-[11rem]"
            />
          </div>
          <button
            type="button"
            className="text-sm text-indigo-700 hover:underline pb-2"
            onClick={() => {
              setFrom(istDaysAgoKey(6));
              setTo(istTodayKey());
            }}
          >
            Last 7 days (IST)
          </button>
          <button
            type="button"
            className="text-sm text-indigo-700 hover:underline pb-2"
            onClick={() => {
              setFrom(istMonthStartKey());
              setTo(istTodayKey());
            }}
          >
            Month to date
          </button>
          {isManager ? (
            <div className="space-y-1 min-w-[12rem]">
              <Label>Salesperson</Label>
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All team (consolidated)</SelectItem>
                  {(data?.reps ?? []).map((r) => (
                    <SelectItem key={r.rep.id} value={r.rep.id}>
                      {r.rep.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground pb-2"
            onClick={() => void refetch()}
          >
            Refresh
          </button>
        </CardContent>
      </Card>

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading outcomes…
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardContent className="pt-4">
                <KpiTile
                  label="DNP agent — leads revived"
                  count={data?.totals.dnpRevived ?? 0}
                  accent="text-amber-800"
                  onClick={() => openDrill("dnpRevived")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <KpiTile
                  label="DNP agent — OPD booked"
                  count={data?.totals.dnpOpdBooked ?? 0}
                  accent="text-amber-800"
                  onClick={() => openDrill("dnpOpdBooked")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <KpiTile
                  label="CRM agent — OPD booked"
                  count={data?.totals.crmOpdBooked ?? 0}
                  onClick={() => openDrill("crmOpdBooked")}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <KpiTile
                  label="Reminder agent — appointment confirmed"
                  count={data?.totals.reminderConfirmed ?? 0}
                  onClick={() => openDrill("reminderConfirmed")}
                />
              </CardContent>
            </Card>
          </div>

          {!isLoading &&
          (data?.totals.dnpRevived ?? 0) +
            (data?.totals.dnpOpdBooked ?? 0) +
            (data?.totals.crmOpdBooked ?? 0) +
            (data?.totals.reminderConfirmed ?? 0) ===
            0 &&
          data?.meta ? (
            <p className="text-sm text-muted-foreground rounded-lg border bg-muted/30 px-4 py-3">
              {data.meta.completedCallsOnLeads > 0 ? (
                <>
                  {data.meta.completedCallsOnLeads} completed AI voice call
                  {data.meta.completedCallsOnLeads === 1 ? "" : "s"} on your leads in this range, but none
                  had a counted outcome (revived / OPD booked on call / reminder confirmed). Try widening
                  the dates or check the Voice AI call log in hospital admin.
                </>
              ) : (
                <>
                  No completed AI voice calls on your assigned leads in this IST range. Widen the date range,
                  or confirm those leads are assigned to you and calls have finished processing.
                </>
              )}
            </p>
          ) : null}

          {showTeamTable ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Team breakdown</CardTitle>
                <CardDescription>Distinct leads per rep in the selected IST range</CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Rep</TableHead>
                      <TableHead className="text-right">DNP revived</TableHead>
                      <TableHead className="text-right">DNP OPD booked</TableHead>
                      <TableHead className="text-right">CRM OPD booked</TableHead>
                      <TableHead className="text-right">Reminder confirmed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(data?.reps ?? []).map((row) => (
                      <TableRow key={row.rep.id}>
                        <TableCell>
                          <div className="font-medium">{row.rep.name}</div>
                          {row.rep.email ? (
                            <div className="text-xs text-muted-foreground">{row.rep.email}</div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.counts.dnpRevived}
                            accent="text-amber-800"
                            onClick={() =>
                              openDrill("dnpRevived", {
                                staffId: row.rep.id,
                                repLabel: row.rep.name,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.counts.dnpOpdBooked}
                            accent="text-amber-800"
                            onClick={() =>
                              openDrill("dnpOpdBooked", {
                                staffId: row.rep.id,
                                repLabel: row.rep.name,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.counts.crmOpdBooked}
                            onClick={() =>
                              openDrill("crmOpdBooked", {
                                staffId: row.rep.id,
                                repLabel: row.rep.name,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.counts.reminderConfirmed}
                            onClick={() =>
                              openDrill("reminderConfirmed", {
                                staffId: row.rep.id,
                                repLabel: row.rep.name,
                              })
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Day-wise (IST)</CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">DNP revived</TableHead>
                    <TableHead className="text-right">DNP OPD booked</TableHead>
                    <TableHead className="text-right">CRM OPD booked</TableHead>
                    <TableHead className="text-right">Reminder confirmed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.byDay ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground">
                        No qualifying AI outcomes in this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (data?.byDay ?? []).map((row) => (
                      <TableRow key={row.date}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.dnpRevived}
                            accent="text-amber-800"
                            onClick={() => openDrill("dnpRevived", { date: row.date })}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.dnpOpdBooked}
                            accent="text-amber-800"
                            onClick={() => openDrill("dnpOpdBooked", { date: row.date })}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.crmOpdBooked}
                            onClick={() => openDrill("crmOpdBooked", { date: row.date })}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <CountLink
                            count={row.reminderConfirmed}
                            onClick={() => openDrill("reminderConfirmed", { date: row.date })}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recent outcomes (newest first)</CardTitle>
              <CardDescription>
                {isManager && repFilter === "__all__"
                  ? "All team · click a row to open the lead"
                  : "Your leads · click a row to open the lead"}
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto max-h-[min(28rem,50vh)] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When (IST)</TableHead>
                    <TableHead>Outcome</TableHead>
                    <TableHead>Patient</TableHead>
                    {isManager && repFilter === "__all__" ? <TableHead>Rep</TableHead> : null}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timeline.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={isManager && repFilter === "__all__" ? 4 : 3}
                        className="text-muted-foreground"
                      >
                        No outcomes yet in this range.
                      </TableCell>
                    </TableRow>
                  ) : (
                    timeline.map((row, idx) => (
                      <TableRow key={`${row.leadId}-${row.completedAt}-${row.metric}-${idx}`}>
                        <TableCell className="text-xs whitespace-nowrap">
                          {formatDateTime(row.completedAt)}
                        </TableCell>
                        <TableCell className="text-sm">{row.metricLabel}</TableCell>
                        <TableCell>
                          <Link
                            to={`/sales-person/leads?openLeadId=${encodeURIComponent(row.leadId)}`}
                            className="font-medium text-indigo-700 hover:underline"
                          >
                            {row.patientName?.trim() || "Lead"}
                          </Link>
                          {row.phone ? (
                            <div className="text-xs text-muted-foreground">{row.phone}</div>
                          ) : null}
                        </TableCell>
                        {isManager && repFilter === "__all__" ? (
                          <TableCell className="text-sm text-muted-foreground">
                            {row.repName ?? "—"}
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}

      <Dialog open={drill != null} onOpenChange={(open) => !open && setDrill(null)}>
        <DialogContent className="max-w-md max-h-[min(70vh,32rem)] flex flex-col">
          <DialogHeader>
            <DialogTitle>{drill?.title ?? "Leads"}</DialogTitle>
          </DialogHeader>
          {drillQuery.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading leads…
            </div>
          ) : (
            <ul className="overflow-y-auto space-y-2 pr-1 -mr-1">
              {(drillQuery.data ?? []).map((lead) => (
                <li key={lead.id}>
                  <Link
                    to={`/sales-person/leads?openLeadId=${encodeURIComponent(lead.id)}`}
                    className="flex flex-col rounded-md border px-3 py-2 hover:bg-muted/60 transition-colors"
                    onClick={() => setDrill(null)}
                  >
                    <span className="font-medium">{lead.patientName?.trim() || "Lead"}</span>
                    {lead.phone ? (
                      <span className="text-xs text-muted-foreground">{lead.phone}</span>
                    ) : null}
                    {lead.sarvamOutcomeAt ? (
                      <span className="text-xs text-muted-foreground">
                        {formatDateTime(lead.sarvamOutcomeAt)}
                      </span>
                    ) : null}
                  </Link>
                </li>
              ))}
              {(drillQuery.data ?? []).length === 0 && !drillQuery.isLoading ? (
                <li className="text-sm text-muted-foreground py-2">No leads in this slice.</li>
              ) : null}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
